import type { Route } from '../admin-fetch';
import { ADMIN_API, bodyOf, call, failure, queryOf, seg, toRawResponse } from '../respond';

// Users, groups and expenses. Lists pass their filters through as the
// query string; the server answers in the shape the tables already read.

const USERS = `${ADMIN_API}/users`;

/** An action on one user that the server takes as `{ userId, … }`. */
function userAction(serverPath: string, extra: (body: Record<string, unknown>) => object = () => ({})): Route[2] {
  return async (req, { id }) =>
    toRawResponse(await call('POST', `${ADMIN_API}/${serverPath}`, { userId: id, ...extra(await bodyOf(req)) }));
}

/** Same, for the two actions whose screens read only `{ message }`. */
function userMessageAction(serverPath: string): Route[2] {
  return async (_req, { id }) => {
    const result = await call<{ message: string }>('POST', `${ADMIN_API}/${serverPath}`, { userId: id });
    return result.ok ? Response.json({ message: result.data.message }) : failure(result);
  };
}

export const peopleRoutes: Route[] = [
  // ── users
  ['GET', '/api/users', async (req) => toRawResponse(await call('GET', `${USERS}${queryOf(req)}`))],
  // { userId, action: 'enable' | 'disable' } — sets email-verified.
  ['PATCH', '/api/users', async (req) => {
    const { userId, action } = await bodyOf<{ userId?: string; action?: string }>(req);
    if (action !== 'enable' && action !== 'disable') return Response.json({ success: true });
    const result = await call('PATCH', `${USERS}/${seg(userId ?? '')}/email-verified`, {
      emailVerified: action === 'enable',
    });
    return result.ok ? Response.json({ success: true }) : failure(result);
  }],
  ['POST', '/api/users/:id/force-logout', async (_req, { id }) =>
    toRawResponse(await call('POST', `${USERS}/${seg(id)}/force-logout`))],
  ['POST', '/api/users/:id/suspend', userAction('suspend', (body) => ({ reason: body.reason }))],
  ['POST', '/api/users/:id/unsuspend', userAction('unsuspend')],
  ['POST', '/api/users/:id/delete', userAction('delete-user', (body) => ({ anonymize: !!body.anonymize }))],
  ['POST', '/api/users/:id/resend-verification', userMessageAction('resend-verification')],
  ['POST', '/api/users/:id/send-password-reset', userMessageAction('send-password-reset')],

  // ── groups
  ['GET', '/api/groups', async (req) => toRawResponse(await call('GET', `${ADMIN_API}/groups${queryOf(req)}`))],
  ['POST', '/api/groups/:id/revoke-invite-links', async (_req, { id }) =>
    toRawResponse(await call('POST', `${ADMIN_API}/groups/${seg(id)}/revoke-invite-links`))],

  // ── expenses
  ['GET', '/api/expenses', async (req) => toRawResponse(await call('GET', `${ADMIN_API}/expenses${queryOf(req)}`))],
];
